import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Suspense, useMemo, useState } from 'react';

import { Card } from '../../components/Card';
import { DonutShare } from '../../components/charts';
import { ExportMenu } from '../../components/ExportMenu';
import { FilterBar } from '../../components/FilterBar';
import { Select } from '../../components/Select';
import { DataTable } from '../../components/Table';
import type { VatDetailedProductRow, VatMonthlyRow } from '../../data/fetchers';
import {
  getSettings,
  getVatDetailedReport,
  getVatSummary,
} from '../../data/fetchers';
import { CHART_H } from '../../lib/chartTokens';
import type { ExportCol } from '../../lib/export';
import { fmtAmount, fmtBahtShort } from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';
import { ReportHeader } from './ReportHeader';

export function resolveVatMonth(
  requestedMonth: string,
  rows: VatMonthlyRow[],
): string {
  if (rows.some((row) => row.month === requestedMonth)) return requestedMonth;
  return rows.at(-1)?.month ?? '';
}

interface VatMonthlyExportRow {
  month: string;
  exVatBaht: string;
  exVatSatang: number;
  vatBaht: string;
  vatSatang: number;
  grossBaht: string;
  grossSatang: number;
  status: string;
}

interface VatDetailedExportRow {
  productId: string;
  name: string;
  qty: number;
  grossBaht: string;
  grossSatang: number;
  exVatBaht: string;
  exVatSatang: number;
  vatBaht: string;
  vatSatang: number;
}

const exportCols: ExportCol<VatMonthlyExportRow>[] = [
  { key: 'month', header: 'เดือน' },
  { key: 'exVatBaht', header: 'ก่อน VAT' },
  { key: 'exVatSatang', header: 'ก่อน VAT (satang)' },
  { key: 'vatBaht', header: 'VAT' },
  { key: 'vatSatang', header: 'VAT (satang)' },
  { key: 'grossBaht', header: 'รวม' },
  { key: 'grossSatang', header: 'รวม (satang)' },
  { key: 'status', header: 'สถานะ' },
];

const detailedExportCols: ExportCol<VatDetailedExportRow>[] = [
  { key: 'productId', header: 'รหัสสินค้า' },
  { key: 'name', header: 'ชื่อสินค้า' },
  { key: 'qty', header: 'จำนวนที่ขาย' },
  { key: 'grossBaht', header: 'ราคารวม' },
  { key: 'grossSatang', header: 'ราคารวม (satang)' },
  { key: 'exVatBaht', header: 'ก่อน VAT' },
  { key: 'exVatSatang', header: 'ก่อน VAT (satang)' },
  { key: 'vatBaht', header: 'VAT 7%' },
  { key: 'vatSatang', header: 'VAT 7% (satang)' },
];

const detailedColumns: ColumnDef<VatDetailedProductRow, unknown>[] = [
  { accessorKey: 'productId', header: 'รหัสสินค้า' },
  { accessorKey: 'name', header: 'ชื่อสินค้า' },
  {
    accessorKey: 'qty',
    header: 'จำนวนที่ขาย',
    cell: (c) => (
      <span className="num">{c.getValue<number>().toLocaleString()}</span>
    ),
  },
  {
    accessorKey: 'gross',
    header: 'ราคารวม',
    cell: (c) => <span className="num">{fmtAmount(c.getValue<number>())}</span>,
  },
  {
    accessorKey: 'exVat',
    header: 'ก่อน VAT',
    cell: (c) => <span className="num">{fmtAmount(c.getValue<number>())}</span>,
  },
  {
    accessorKey: 'vat',
    header: 'VAT 7%',
    cell: (c) => <span className="num">{fmtAmount(c.getValue<number>())}</span>,
  },
];

export function VatSummaryView() {
  const { scope, ready } = useBranchScope();
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
  });
  const vatRate = settings?.vatRate ?? 0.07;

  const [requestedMonth, setRequestedMonth] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: [
      'report',
      'vat',
      scope.branchId,
      scope.allowedBranchIds,
      vatRate,
    ],
    queryFn: () => getVatSummary(scope, vatRate),
    enabled: ready && !!settings,
  });

  const rows = useMemo<VatMonthlyRow[]>(() => data?.rows ?? [], [data]);
  const selectedMonth = resolveVatMonth(requestedMonth, rows);

  const { data: detailedData, isLoading: isDetailLoading } = useQuery({
    queryKey: [
      'report',
      'vat-detail',
      selectedMonth,
      scope.branchId,
      scope.allowedBranchIds,
      vatRate,
    ],
    queryFn: () => getVatDetailedReport(selectedMonth, scope, vatRate),
    enabled: ready && !!settings && !!selectedMonth,
  });

  const donutData = useMemo(
    () =>
      data
        ? [
            { label: 'ก่อน VAT', value: data.exVat },
            { label: 'VAT', value: data.vat },
          ]
        : [],
    [data],
  );

  const exportRows = useMemo<VatMonthlyExportRow[]>(
    () =>
      rows.map((row) => ({
        month: row.month,
        exVatBaht: fmtAmount(row.exVat),
        exVatSatang: row.exVat,
        vatBaht: fmtAmount(row.vat),
        vatSatang: row.vat,
        grossBaht: fmtAmount(row.gross),
        grossSatang: row.gross,
        status: row.status,
      })),
    [rows],
  );

  const columns = useMemo<ColumnDef<VatMonthlyRow, unknown>[]>(
    () => [
      { accessorKey: 'month', header: 'เดือน' },
      {
        accessorKey: 'exVat',
        header: 'ก่อน VAT',
        cell: (c) => (
          <span className="num">{fmtAmount(c.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'vat',
        header: 'VAT',
        cell: (c) => (
          <span className="num">{fmtAmount(c.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'gross',
        header: 'รวม',
        cell: (c) => (
          <span className="num">{fmtAmount(c.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'สถานะ',
        cell: (c) => {
          const val = c.getValue<string>();
          const filed = val === 'ยื่นแล้ว';
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                filed
                  ? 'bg-ok-light text-ok dark:bg-ok/20 dark:text-ok'
                  : 'bg-warn-light text-warn dark:bg-warn/20 dark:text-warn'
              }`}
            >
              {val}
            </span>
          );
        },
      },
    ],
    [],
  );

  const filteredDetailedRows = detailedData?.rows ?? [];
  const detailedExportRows = useMemo<VatDetailedExportRow[]>(
    () =>
      filteredDetailedRows.map((row) => ({
        productId: row.productId,
        name: row.name,
        qty: row.qty,
        grossBaht: fmtAmount(row.gross),
        grossSatang: row.gross,
        exVatBaht: fmtAmount(row.exVat),
        exVatSatang: row.exVat,
        vatBaht: fmtAmount(row.vat),
        vatSatang: row.vat,
      })),
    [filteredDetailedRows],
  );

  return (
    <div className="bo-fade space-y-6">
      <ReportHeader
        title="สรุป VAT"
        subtitle={
          settings
            ? `${settings.shopName} · เลขผู้เสียภาษี ${settings.taxId}`
            : undefined
        }
        status={{
          label:
            !ready || isLoading ? 'กำลังประมวลผล' : `${rows.length} เดือนภาษี`,
          tone: !ready || isLoading ? 'neutral' : 'ok',
        }}
        actions={
          <ExportMenu
            rows={exportRows}
            cols={exportCols}
            filename="สรุป VAT"
            title="สรุป VAT"
            pdf
          />
        }
      />

      <section
        className="overflow-hidden rounded-[20px] border border-line-2 bg-surface"
        aria-labelledby="selected-vat-month-heading"
      >
        <div className="grid gap-5 border-b border-line-2 bg-surface-2/40 px-4 py-5 sm:px-6 lg:grid-cols-[minmax(240px,0.55fr)_minmax(0,1.45fr)] lg:items-end">
          <FilterBar compact>
            <label className="flex min-w-0 flex-col gap-2">
              <span className="text-sm-label font-bold uppercase tracking-[0.1em] text-ink-3">
                เดือนที่ต้องการตรวจ
              </span>
              <Select
                aria-label="เดือนภาษี"
                value={selectedMonth}
                onChange={(event) => {
                  setRequestedMonth(event.target.value);
                }}
                disabled={!rows.length}
              >
                {rows.length === 0 ? (
                  <option value="">ยังไม่มีเดือนภาษี</option>
                ) : (
                  rows.map((row) => (
                    <option key={row.month} value={row.month}>
                      {row.month} · {row.status}
                    </option>
                  ))
                )}
              </Select>
            </label>
          </FilterBar>
          <div className="min-w-0">
            <div className="text-sm-label font-bold uppercase tracking-[0.12em] text-primary">
              ผลของเดือนที่เลือก
            </div>
            <h2
              id="selected-vat-month-heading"
              className="mt-1 text-h2 font-bold text-ink"
            >
              ภาษีขายเดือน {selectedMonth || '—'}
            </h2>
            <p className="mt-1 text-body text-ink-3">
              เลือกเดือนแล้วตรวจยอดสรุปและรายการสินค้า
              และส่งออกรายละเอียดได้ในส่วนเดียวกัน
            </p>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <div className="mb-5 grid overflow-hidden rounded-[16px] border border-line bg-line sm:grid-cols-[1.15fr_0.85fr_0.85fr_0.65fr]">
            {[
              [
                'ยอดขายรวม',
                detailedData ? fmtAmount(detailedData.gross) : '—',
                'บาท',
              ],
              [
                'ยอดก่อน VAT',
                detailedData ? fmtAmount(detailedData.exVat) : '—',
                'บาท',
              ],
              [
                'VAT 7%',
                detailedData ? fmtAmount(detailedData.vat) : '—',
                'บาท',
              ],
              [
                'สินค้าที่ขาย',
                detailedData ? detailedData.rows.length.toString() : '—',
                'รายการ',
              ],
            ].map(([label, value, unit]) => (
              <div key={label} className="min-w-0 bg-surface px-4 py-4">
                <div className="text-sm-label font-medium text-ink-2">
                  {label}
                </div>
                <div className="mt-1 flex min-w-0 flex-wrap items-baseline gap-1.5">
                  <span className="num break-words text-xl font-semibold text-ink">
                    {value}
                  </span>
                  <span className="text-sm-label text-ink-2">{unit}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mb-5 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {detailedData && (
              <ExportMenu
                rows={detailedExportRows}
                cols={detailedExportCols}
                filename={`รายละเอียด VAT 7% - เดือน ${selectedMonth}`}
                title={`รายละเอียด VAT 7% - เดือน ${selectedMonth}`}
                pdf
              />
            )}
          </div>
          {!ready || !settings || isDetailLoading ? (
            <div className="h-40 animate-pulse rounded-card bg-bg-warm" />
          ) : (
            <DataTable
              pageSize={Number.MAX_SAFE_INTEGER}
              columns={detailedColumns}
              data={filteredDetailedRows}
              emptyMessage="ไม่พบข้อมูลสินค้าสำหรับเดือนนี้"
              mobileCards
            />
          )}
        </div>
      </section>

      <section
        className="grid items-start gap-5 lg:grid-cols-[minmax(280px,0.75fr)_minmax(0,1.25fr)]"
        aria-labelledby="vat-history-heading"
      >
        <Card title="สัดส่วนภาษีทุกเดือน" className="h-full">
          <Suspense
            fallback={
              <div
                className="bo-card animate-pulse"
                style={{ height: CHART_H.md }}
              />
            }
          >
            <DonutShare data={donutData} valueFormat={fmtBahtShort} />
          </Suspense>
        </Card>
        <div className="min-w-0">
          <div className="mb-3">
            <div className="text-sm-label font-bold uppercase tracking-[0.12em] text-primary">
              ประวัติย้อนหลัง
            </div>
            <h2
              id="vat-history-heading"
              className="mt-1 text-h3 font-bold text-ink"
            >
              สถานะการยื่นรายเดือน
            </h2>
          </div>
          {!ready || !settings || isLoading ? (
            <div className="bo-card h-64 animate-pulse" />
          ) : (
            <DataTable
              pageSize={Number.MAX_SAFE_INTEGER}
              columns={columns}
              data={rows}
              onRowClick={(row) => setRequestedMonth(row.month)}
              mobileCards
            />
          )}
        </div>
      </section>
    </div>
  );
}
